import get from "lodash.get";

import {
  CheckoutBlockStyles,
  CheckoutBlockVariables,
  PostPurchaseUpsellCheckoutBlockDto,
} from "@intelligems/ig-types";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@src/store/rootState";
import {
  CheckoutExtensionBlockDto,
  ICheckoutBlocksState,
} from "@src/store/slices/checkoutBlocks/types";

const getCheckoutBlocks = (state: RootState) =>
  state.checkoutBlocks.checkoutBlocks;

export function getRootCheckoutBlock(state: ICheckoutBlocksState, id: string) {
  return state.checkoutBlocks.find((i) => i.id === id);
}
const getCheckoutBlocksMap = createSelector(
  getCheckoutBlocks,
  (checkoutBlocks) =>
    checkoutBlocks.reduce(
      (map, checkoutBlock) => {
        const { id } = checkoutBlock;
        map[id] = checkoutBlock;
        return map;
      },
      {} as Record<string, CheckoutExtensionBlockDto>
    )
);

export const getCheckoutBlock =
  (checkoutBlockId: string) => (state: RootState) =>
    getCheckoutBlocksMap(state)[checkoutBlockId] as
      | CheckoutExtensionBlockDto
      | undefined;

export const getCheckoutBlocksByVariationId = (variationId: string) =>
  createSelector(
    (state: RootState) => state.checkoutBlocks.checkoutBlocks,
    (checkoutBlocks) =>
      checkoutBlocks.filter(
        (checkoutBlock) => checkoutBlock.variationId === variationId
      )
  );

export const getUpsellCheckoutBlockIds = createSelector(
  getCheckoutBlocks,
  (checkoutBlocks) =>
    checkoutBlocks
      .filter((checkoutBlock) => checkoutBlock.blockType === "upsell")
      .map((checkoutBlock) => checkoutBlock.id)
);

export const getHasAnyUpsellBlocks = () =>
  createSelector(
    (state: RootState) => state.checkoutBlocks,
    (checkoutBlocks) =>
      checkoutBlocks.checkoutBlocks.some(
        (checkoutBlock) => checkoutBlock.blockType === "upsell"
      ) || false
  );

// Gates the "Viewed Checkout Block" filter option: every block type emits the
// same view event, so any block on the store makes the stage meaningful.
export const getHasAnyCheckoutBlocks = () =>
  createSelector(
    (state: RootState) => state.checkoutBlocks.checkoutBlocks,
    (checkoutBlocks) => checkoutBlocks.length > 0
  );

// Whether every variation carries a checkout block of ANY type. Drives the
// default conversion stage: preselecting the block-viewed stage is only fair
// when no variation is excluded from the population by construction.
export const getEveryVariationHasAnyCheckoutBlock = (variationIds: string[]) =>
  createSelector(
    (state: RootState) => state.checkoutBlocks.checkoutBlocks,
    (checkoutBlocks) =>
      variationIds.length > 0 &&
      variationIds.every((variationId) =>
        checkoutBlocks.some((block) => block.variationId === variationId)
      )
  );

export const getEveryVariationHasPostPurchase = (variationIds: string[]) =>
  createSelector(
    (state: RootState) => state.checkoutBlocks.postPurchaseBlocks,
    (postPurchaseBlocks) =>
      variationIds.length > 0 &&
      variationIds.every((variationId) =>
        postPurchaseBlocks.some((block) => block.variationId === variationId)
      )
  );

export const getCheckoutBlockVariable =
  (checkoutBlockId: string, key: string) => (state: RootState) => {
    return get(
      getCheckoutBlock(checkoutBlockId)(state)?.variables,
      key
    ) as CheckoutBlockVariables;
  };

export const getCheckoutBlockStyle =
  (checkoutBlockId: string, key: string) => (state: RootState) => {
    return get(
      getCheckoutBlock(checkoutBlockId)(state)?.styles,
      key
    ) as CheckoutBlockStyles;
  };

export const getCheckoutBlockNameOrBlockType =
  (checkoutBlockId: string, key: string) => (state: RootState) => {
    return get(getCheckoutBlock(checkoutBlockId)(state), key) as Pick<
      CheckoutExtensionBlockDto,
      "name" | "blockType"
    >;
  };

export const getCheckoutBlockType =
  (checkoutBlockId: string) => (state: RootState) => {
    return getCheckoutBlock(checkoutBlockId)(state)?.blockType;
  };

export const getPostPurchaseBlocks = (state: RootState) =>
  state.checkoutBlocks.postPurchaseBlocks;

export const getPostPurchaseBlocksByVariationId = (variationId: string) =>
  createSelector(
    (state: RootState) => state.checkoutBlocks.postPurchaseBlocks,
    (postPurchaseBlocks) =>
      postPurchaseBlocks.filter((block) => block.variationId === variationId)
  );

export const getPostPurchaseBlockForVariation =
  (variationId: string) => (state: RootState) =>
    state.checkoutBlocks.postPurchaseBlocks.find(
      (b) => b.variationId === variationId
    ) ?? null;

export const getCurrentPostPurchaseBlockId = (state: RootState) =>
  state.checkoutBlocks.currentPostPurchaseBlockId;

export const getPostPurchaseBlock = (blockId: string) => (state: RootState) =>
  state.checkoutBlocks.postPurchaseBlocks.find((b) => b.id === blockId);

export const getCurrentPostPurchaseBlock = (
  state: RootState
): PostPurchaseUpsellCheckoutBlockDto | null => {
  const id = state.checkoutBlocks.currentPostPurchaseBlockId;
  if (!id) return null;
  return (
    state.checkoutBlocks.postPurchaseBlocks.find((b) => b.id === id) ?? null
  );
};

export const getPostPurchaseVariable =
  (blockId: string, key: string) => (state: RootState) => {
    const block = state.checkoutBlocks.postPurchaseBlocks.find(
      (b) => b.id === blockId
    );
    return get(block?.variables, key);
  };

export const getPostPurchaseStyle =
  (blockId: string, key: string) => (state: RootState) => {
    const block = state.checkoutBlocks.postPurchaseBlocks.find(
      (b) => b.id === blockId
    );
    return get(block?.styles, key);
  };

export const getCartTesterProducts =
  (variationId: string) => (state: RootState) =>
    state.checkoutBlocks.cartTesterProductsByVariation[variationId] ?? [];

export const didUserEditCartTester =
  (variationId: string) => (state: RootState) =>
    state.checkoutBlocks.cartTesterEditedByUserByVariation[variationId] ??
    false;

export const getHasShippingProgressBar = createSelector(
  getCheckoutBlocks,
  (checkoutBlocks) =>
    checkoutBlocks.some(
      (checkoutBlock) => checkoutBlock.blockType === "shippingProgressBar"
    )
);
